import { useMemo, useState } from "react";

import {
  PageContainer,
  type ComponentDiagnostic,
} from "@swot/base-components";

import type { EnterpriseNotification } from "@swot/swot-components";

import {
  CmtsSmartDataGrid,
  type CmtsGridColumn,
} from "@components/data-grid/index.js";

import { CmtsPageHintPanel } from "@components/hints/index.js";
import { CmtsEnterpriseModal } from "@components/CmtsEnterpriseModal.js";
import { CmtsEnterpriseForm } from "@components/forms/index.js";

type NotificationRow =
  EnterpriseNotification & Record<string, unknown>;

type ComplaintOption = { readonly value: string; readonly label: string };

const GUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

function normalizedCode(value: string): string {
  return value.trim().toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "") || "default";
}

function displayLabel(value: string): string {
  return value.trim().replace(/[_-]+/g, " ") || "Unknown";
}

function cleanNotificationTitle(row: Record<string, unknown>): string {
  const raw = text(row, ["title", "subject", "message"], "CMTS notification").trim();
  const complaintMatch = raw.match(/Complaint\s+(CMP-[A-Z0-9-]+)/i);
  if (complaintMatch?.[1]) return `Complaint ${complaintMatch[1].toUpperCase()}`;

  const eventType = text(row, ["eventType", "notificationType", "sourceModule"]);
  let cleaned = raw;
  if (eventType) {
    const escaped = eventType.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    cleaned = cleaned.replace(new RegExp(`(?:[:\\s]*)${escaped}(?:[:\\s]*${escaped})*$`, "i"), "").trim();
  }
  const half = cleaned.slice(0, cleaned.length / 2);
  if (cleaned.length % 2 === 0 && half && `${half}${half}`.toLowerCase() === cleaned.toLowerCase()) return half.trim();
  return cleaned || "CMTS notification";
}

function priorityLabel(row: Record<string, unknown>): string {
  for (const key of ["priorityCode", "priorityName", "priorityLabel", "severityName", "severityCode", "severity", "priority"]) {
    const value = text(row, [key]);
    if (value && !GUID_PATTERN.test(value)) return displayLabel(value);
  }
  return "Normal";
}

function complaintLabel(row: Record<string, unknown>, options: readonly ComplaintOption[]): string {
  const code = text(row, ["linkedRecordCode", "complaintNumber", "complaintCode", "registrationNumber", "referenceNumber"]);
  if (code && !GUID_PATTERN.test(code)) return code;
  const id = text(row, ["complaintId", "parentComplaintId", "linkedEntityId", "sourceEntityId"]);
  if (!id) return "No complaint linked";
  const option = options.find((item) => item.value.toLowerCase() === id.toLowerCase());
  return option?.label.split("—")[0]?.trim() || "Complaint linked";
}

function text(
  row: Record<string, unknown>,
  keys: readonly string[],
  fallback = "",
): string {
  for (const key of keys) {
    const value = row[key];

    if (
      value !== undefined &&
      value !== null &&
      String(value).trim()
    ) {
      return String(value);
    }
  }

  return fallback;
}

function notificationId(
  row: Record<string, unknown>,
): string {
  return text(row, ["id", "notificationId"]);
}

function lifecycleStatus(
  row: Record<string, unknown>,
): string {
  const explicitState = text(row, [
    "notificationState",
    "readState",
  ]);

  if (explicitState) {
    return explicitState.toUpperCase();
  }

  if (text(row, ["readAt", "readAtUtc"])) {
    return "READ";
  }

  const fallbackStatus = text(
    row,
    ["status"],
    "UNREAD",
  ).toUpperCase();

  // Delivery states are treated as unread lifecycle states.
  if (
    [
      "PENDING",
      "QUEUED",
      "SENT",
      "DELIVERED",
    ].includes(fallbackStatus)
  ) {
    return "UNREAD";
  }

  return fallbackStatus;
}

function deliveryStatus(
  row: Record<string, unknown>,
): string {
  const explicitDelivery = text(row, [
    "deliveryStatus",
    "channelStatus",
  ]);

  if (explicitDelivery) {
    return explicitDelivery;
  }

  const status = text(row, ["status"], "QUEUED");
  const normalized = status.toUpperCase();

  if (
    [
      "READ",
      "UNREAD",
      "ACKNOWLEDGED",
      "ARCHIVED",
      "ACTIONED",
    ].includes(normalized)
  ) {
    return text(
      row,
      ["lastDeliveryStatus"],
      "DELIVERED",
    );
  }

  return status;
}

function isArchived(
  row: Record<string, unknown>,
): boolean {
  return (
    lifecycleStatus(row) === "ARCHIVED" ||
    Boolean(
      row.archivedAt ??
        row.archivedAtUtc,
    )
  );
}

function isUnread(
  row: Record<string, unknown>,
): boolean {
  const state = lifecycleStatus(row);

  return (
    ![
      "READ",
      "ACKNOWLEDGED",
      "ARCHIVED",
      "ACTIONED",
    ].includes(state) &&
    !text(row, ["readAt", "readAtUtc"])
  );
}

function formatDateTime(
  row: Record<string, unknown>,
): string {
  const value = text(row, [
    "createdAt",
    "raisedAt",
    "createdAtUtc",
  ]);

  if (!value) {
    return "Date unavailable";
  }

  const date = new Date(value);

  return Number.isNaN(date.getTime())
    ? "Date unavailable"
    : new Intl.DateTimeFormat("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit", hour12: true }).format(date);
}

type NotificationActionCardProps = {
  icon: string;
  title: string;
  description: string;
  disabled?: boolean;
  danger?: boolean;
  onClick: () => void;
};

function NotificationActionCard({
  icon,
  title,
  description,
  disabled = false,
  danger = false,
  onClick,
}: NotificationActionCardProps) {
  return (
    <button
      type="button"
      className={[
        "cmts-notification-action-option",
        danger
          ? "cmts-notification-action-option--danger"
          : "",
      ]
        .filter(Boolean)
        .join(" ")}
      disabled={disabled}
      onClick={onClick}
    >
      <span
        className="cmts-notification-action-option__icon"
        aria-hidden="true"
      >
        {icon}
      </span>

      <span className="cmts-notification-action-option__content">
        <strong>{title}</strong>
        <small>{description}</small>
      </span>

      <span
        className="cmts-notification-action-option__arrow"
        aria-hidden="true"
      >
        ›
      </span>
    </button>
  );
}

const taskFields = [
  {
    name: "title",
    label: "Task title",
    required: true,
    minLength: 4,
    placeholder: "Follow up notification",
  },
  {
    name: "remarks",
    label: "Reason / context",
    type: "textarea" as const,
    required: true,
    minLength: 4,
  },
];

export function CmtsNotificationsPage(props: {
  notifications?:
    | readonly EnterpriseNotification[]
    | undefined;

  complaintOptions?: readonly ComplaintOption[] | undefined;

  diagnostics?:
    | readonly ComponentDiagnostic[]
    | undefined;

  busy?: boolean | undefined;
  error?: string | null | undefined;

  onMarkRead: (
    notification: EnterpriseNotification,
  ) => void | Promise<void>;

  onMarkUnread: (
    notification: EnterpriseNotification,
  ) => void | Promise<void>;

  onAcknowledge: (
    notification: EnterpriseNotification,
  ) => void | Promise<void>;

  onArchive: (
    notification: EnterpriseNotification,
  ) => void | Promise<void>;

  onExecuteAction: (
    notification: EnterpriseNotification,
  ) => void | Promise<void>;

  onCreateTask: (
    notification: EnterpriseNotification,
    payload: Record<string, unknown>,
  ) => void | Promise<void>;

  onOpenLinkedRecord: (
    notification: EnterpriseNotification,
  ) => void | Promise<void>;
}) {
  const [actionsFor, setActionsFor] =
    useState<NotificationRow | null>(null);

  const [taskFor, setTaskFor] =
    useState<NotificationRow | null>(null);

  const rows =
    (props.notifications ??
      []) as readonly NotificationRow[];

  const runAction = async (
    action: () => void | Promise<void>,
  ): Promise<void> => {
    try {
      await action();
      setActionsFor(null);
    } catch {
      // Mutation error is exposed through props.error.
      // Keep the modal open so the user can see the error.
    }
  };

  const complaintOptions = props.complaintOptions ?? [];

  const columns = useMemo<readonly CmtsGridColumn<Record<string, unknown>>[]>(() => [
    {
      id: "title",
      header: "Notification",
      sortable: true,
      filterable: true,
      minWidth: 240,
      render: (row) => (
        <div className="cmts-notification-title">
          <strong>
            {cleanNotificationTitle(row)}
          </strong>

          <span>
            Notification
          </span>
        </div>
      ),
    },
    {
      id: "channel",
      header: "Channel",
      sortable: true,
      filterable: true,
      minWidth: 110,
      render: (row) => <span className="cmts-notification-channel">{displayLabel(text(row, ["channel"], "in app"))}</span>,
    },
    {
      id: "notificationState",
      header: "Status",
      sortable: true,
      filterable: true,
      minWidth: 125,
      render: (row) => (
        <span
          className={[
            "cmts-status-chip",
            `cmts-notification-status--${normalizedCode(lifecycleStatus(row))}`,
          ].join(" ")}
        >
          {lifecycleStatus(row)}
        </span>
      ),
    },
    {
      id: "status",
      header: "Delivery",
      sortable: true,
      filterable: true,
      minWidth: 120,
      render: (row) => (
        <span className={`cmts-notification-delivery cmts-notification-delivery--${normalizedCode(deliveryStatus(row))}`}>
          {displayLabel(deliveryStatus(row))}
        </span>
      ),
    },
    {
      id: "priority",
      header: "Priority",
      sortable: true,
      filterable: true,
      minWidth: 110,
      render: (row) => {
        const priority = priorityLabel(row);
        return <span className={`cmts-priority-chip cmts-priority-chip--${normalizedCode(priority)}`}>{priority}</span>;
      },
    },
    {
      id: "linkedEntityType",
      header: "Linked Context",
      sortable: true,
      filterable: true,
      minWidth: 150,
      render: (row) => <span className="cmts-notification-context">{displayLabel(text(row, ["eventType", "sourceModule", "linkedEntityType"], "CMTS"))}</span>,
    },
    {
      id: "linkedEntityId",
      header: "Linked Record",
      sortable: true,
      filterable: true,
      minWidth: 180,
      render: (row) => <span className="cmts-notification-linked-record">{complaintLabel(row, complaintOptions)}</span>,
    },
    {
      id: "createdAt",
      header: "Created",
      sortable: true,
      filterable: true,
      minWidth: 170,
      render: (row) => formatDateTime(row),
    },
    {
      id: "notificationActions",
      header: "Actions",
      sortable: false,
      filterable: false,
      minWidth: 125,
      render: (row) => (
        <button
          type="button"
          className="cmts-notification-actions-button"
          aria-label="Open notification actions"
          onClick={(event) => {
            event.stopPropagation();
            setActionsFor(
              row as NotificationRow,
            );
          }}
        >
          <span
            className="cmts-notification-actions-button__icon"
            aria-hidden="true"
          >
            ⋮
          </span>

          <span>Actions</span>
        </button>
      ),
    },
  ], [complaintOptions]);

  const selectedIsArchived =
    actionsFor !== null &&
    isArchived(actionsFor);

  const selectedIsUnread =
    actionsFor !== null &&
    isUnread(actionsFor);

  const selectedHasLinkedRecord =
    actionsFor !== null &&
    Boolean(
      text(actionsFor, [
        "complaintId",
        "linkedEntityId",
        "sourceEntityId",
      ]),
    );

  const selectedHasExecutableAction =
    actionsFor !== null &&
    Boolean(
      text(actionsFor, [
        "actionCode",
        "complaintId",
        "linkedEntityId",
      ]),
    );

  return (
    <PageContainer
      title="My Notifications"
      subtitle="Repository-backed workflow, SLA, escalation, governance, security and system notifications with linked actions."
    >
      <CmtsPageHintPanel defaultOpen>
        Notifications are linked to source records and persist
        read/unread, acknowledge, archive and action state
        through the backend repository.
      </CmtsPageHintPanel>

      <CmtsSmartDataGrid<Record<string, unknown>>
        title="Notification lifecycle"
        subtitle="Read/unread, action and archive state is persisted and audit-safe."
        rows={rows}
        columns={columns}
        rowKey="id"
        defaultPageSize={10}
        exportable
        filters={[
          {
            id: "notificationState",
            label: "Status",
            columnId: "notificationState",
            operator: "contains",
            placeholder: "All statuses",
          },
          {
            id: "priority",
            label: "Priority",
            columnId: "priority",
            operator: "contains",
            placeholder: "All priorities",
          },
          {
            id: "linkedEntityType",
            label: "Context",
            columnId: "linkedEntityType",
            operator: "contains",
            placeholder: "All contexts",
          },
        ]}
        emptyMessage="No notifications are available for this user."
      />

      {/* Notification actions modal */}
      <CmtsEnterpriseModal
        open={Boolean(actionsFor)}
        title="Notification actions"
        subtitle={
          actionsFor
            ? text(
                actionsFor,
                ["subject", "title", "message"],
                `Notification ${notificationId(
                  actionsFor,
                )}`,
              )
            : ""
        }
        onClose={() => {
          if (!props.busy) {
            setActionsFor(null);
          }
        }}
        size="md"
      >
        {actionsFor && (
          <div className="cmts-notification-action-modal">
            <div className="cmts-notification-action-modal__summary">
              <div>
                <span>Current status</span>
                <strong>
                  {lifecycleStatus(actionsFor)}
                </strong>
              </div>

              <div>
                <span>Context</span>
                <strong>
                  {text(
                    actionsFor,
                    [
                      "eventType",
                      "sourceModule",
                      "linkedEntityType",
                    ],
                    "CMTS",
                  )}
                </strong>
              </div>

              <div>
                <span>Created</span>
                <strong>
                  {formatDateTime(actionsFor)}
                </strong>
              </div>
            </div>

            {props.error && (
              <div
                className="cmts-notification-action-modal__error"
                role="alert"
              >
                {props.error}
              </div>
            )}

            <div className="cmts-notification-action-modal__grid">
              <NotificationActionCard
                icon="↗"
                title="Open linked record"
                description="Open the linked complaint and its relevant workflow section."
                disabled={
                  props.busy ||
                  !selectedHasLinkedRecord
                }
                onClick={() => {
                  const selected = actionsFor;

                  void runAction(() =>
                    props.onOpenLinkedRecord(
                      selected,
                    ),
                  );
                }}
              />

              <NotificationActionCard
                icon="✓"
                title="Mark as read"
                description="Move this notification to the read state."
                disabled={
                  props.busy ||
                  !selectedIsUnread
                }
                onClick={() => {
                  const selected = actionsFor;

                  void runAction(() =>
                    props.onMarkRead(selected),
                  );
                }}
              />

              <NotificationActionCard
                icon="○"
                title="Mark as unread"
                description="Return this notification to the unread state."
                disabled={
                  props.busy ||
                  selectedIsArchived ||
                  selectedIsUnread
                }
                onClick={() => {
                  const selected = actionsFor;

                  void runAction(() =>
                    props.onMarkUnread(selected),
                  );
                }}
              />

              <NotificationActionCard
                icon="★"
                title="Acknowledge"
                description="Confirm that this notification was reviewed."
                disabled={
                  props.busy ||
                  selectedIsArchived
                }
                onClick={() => {
                  const selected = actionsFor;

                  void runAction(() =>
                    props.onAcknowledge(selected),
                  );
                }}
              />

              <NotificationActionCard
                icon="⚡"
                title="Execute action"
                description="Run the linked governed workflow action."
                disabled={
                  props.busy ||
                  selectedIsArchived ||
                  !selectedHasExecutableAction
                }
                onClick={() => {
                  const selected = actionsFor;

                  void runAction(() =>
                    props.onExecuteAction(
                      selected,
                    ),
                  );
                }}
              />

              <NotificationActionCard
                icon="+"
                title="Create task"
                description="Create a follow-up task from this notification."
                disabled={
                  props.busy ||
                  selectedIsArchived
                }
                onClick={() => {
                  const selected = actionsFor;

                  setActionsFor(null);
                  setTaskFor(selected);
                }}
              />
            </div>

            <div className="cmts-notification-action-modal__danger">
              <NotificationActionCard
                icon="×"
                title="Archive notification"
                description="Remove this notification from the active notification lifecycle."
                danger
                disabled={
                  props.busy ||
                  selectedIsArchived
                }
                onClick={() => {
                  const selected = actionsFor;

                  void runAction(() =>
                    props.onArchive(selected),
                  );
                }}
              />
            </div>
          </div>
        )}
      </CmtsEnterpriseModal>

      {/* Existing create-task modal */}
      <CmtsEnterpriseModal
        open={Boolean(taskFor)}
        title="Create task from notification"
        subtitle={`Notification ${
          taskFor
            ? notificationId(taskFor)
            : ""
        }`}
        onClose={() => {
          if (!props.busy) {
            setTaskFor(null);
          }
        }}
        size="md"
      >
        <CmtsEnterpriseForm
          fields={taskFields}
          submitLabel="Create task"
          busy={props.busy}
          error={props.error}
          initialValues={{
            title: taskFor
              ? `Follow up: ${text(
                  taskFor,
                  [
                    "title",
                    "subject",
                    "message",
                  ],
                  "notification",
                )}`
              : "",
            remarks: "",
          }}
          onCancel={() => setTaskFor(null)}
          onSubmit={async (payload) => {
            if (!taskFor) {
              return;
            }

            try {
              await props.onCreateTask(
                taskFor,
                payload,
              );

              setTaskFor(null);
            } catch {
              // Mutation error is displayed through props.error.
            }
          }}
        />
      </CmtsEnterpriseModal>
    </PageContainer>
  );
}